import { useState, useEffect, type RefObject } from 'react';

/** Which axes count as overflow. Narrow this to the axis that the caller
 *  actually clips: a vertically line-clamped paragraph can still report
 *  horizontal overflow from one long unbreakable token (a URL, a code span),
 *  which would otherwise read as "there is more text hidden below". */
export type TOverflowAxis = 'both' | 'horizontal' | 'vertical';

/**
 * Hook to detect whether an element's content overflows its box (i.e. it is
 * visually clipped by `overflow: hidden`, `truncate`, or a fixed height).
 * Re-measures on resize.
 *
 * @param elementRef - React ref to the element to observe
 * @param axis - Axes to measure; defaults to `'both'`
 * @param remeasureKey - Value that forces a re-measure when it changes. The
 *   ResizeObserver only reports changes to the observed *box*, so content that
 *   grows or shrinks without moving that box goes unnoticed — a `line-clamp`ed
 *   element is pinned to N lines, so replacing its text in place keeps the box
 *   identical while the overflow answer flips. Pass whatever the measurement
 *   depends on (usually the text) when the content can change without a remount.
 * @returns True when the element's scroll size exceeds its client size
 */
export function useIsOverflowing(
  elementRef: RefObject<HTMLElement | null>,
  axis: TOverflowAxis = 'both',
  remeasureKey?: unknown,
): boolean {
  const [isOverflowing, setIsOverflowing] = useState(false);

  // eslint-disable-next-line no-restricted-syntax -- reason: ResizeObserver requires imperative API setup and cleanup
  useEffect(() => {
    const element = elementRef?.current;
    if (!element) return;

    const measure = () => {
      const overflowing =
        (axis !== 'vertical' && element.scrollWidth > element.clientWidth) ||
        (axis !== 'horizontal' && element.scrollHeight > element.clientHeight);
      // Only update when the result actually changes to avoid redundant
      // re-renders (and act() noise in tests) on every ResizeObserver tick.
      setIsOverflowing(prev => (prev === overflowing ? prev : overflowing));
    };

    const resizeObserver = new ResizeObserver(measure);
    resizeObserver.observe(element);

    // Initial measurement
    measure();

    return () => {
      resizeObserver.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reason: elementRef.current is the dependency that changes
  }, [elementRef?.current, axis, remeasureKey]);

  return isOverflowing;
}
